/**
 * Dual-cap, ratio-preserving media width (single explicit dimension).
 *
 * Shared owner of the CSS-expression builder used by both the chat media
 * bubble (`MediaBubbleFrame`) and the community media card
 * (`CommunityMessageMediaCard`). It mirrors Flutter's sizing rule
 * (`chat_screen.dart:1297-1311`):
 *   - landscape (`aspectRatio > 1`) → width budget = `75vw` (Flutter
 *     `width * 0.75`)
 *   - portrait / square (`aspectRatio <= 1`) → width budget = `50vw`
 *     (Flutter `width / 2`)
 *
 * The media must fit BOTH a width budget (above) AND a height budget (`50vh`,
 * so a tall thumbnail never overflows the visible area between the sticky app
 * bar and the bottom composer) while always keeping the media's real aspect
 * ratio.
 *
 * A naive `width: budget` + CSS `aspect-ratio` + `max-height: 50vh` does NOT
 * preserve the ratio: `aspect-ratio` only sizes the *unconstrained* dimension,
 * so when the `max-height` cap binds the browser shrinks the height but keeps
 * the explicit width — the box ends up a different ratio than the media and the
 * inner `object-fit: cover` crops it.
 *
 * Instead we make WIDTH the single explicit dimension and pick it so the derived
 * height (`width / aspectRatio`) can never exceed the height budget: the width
 * that produces exactly `50vh` of height is `50vh * aspectRatio`. Taking
 * `min(widthBudget, 50vh * aspectRatio)` guarantees `width ≤ widthBudget` AND
 * `height ≤ 50vh` simultaneously, and because width is the only explicit
 * constraint CSS `aspect-ratio` always derives the height exactly — the ratio
 * is preserved in every orientation, so `object-fit: cover` never crops.
 *
 * Callers keep their own aspect-ratio fallbacks (chat defaults to `1`,
 * community to `9/16`) and pass the already-resolved ratio here.
 */
export const getRatioPreservingMediaWidthCss = (aspectRatio: number): string => {
    const widthBudget = aspectRatio > 1 ? '75vw' : '50vw';
    return `min(${widthBudget}, calc(50vh * ${aspectRatio}))`;
};

export interface RatioPreservingBoxSizeOptions {
    /** The media's own ratio, as carried by `MediaInfoEntity.aspectRatio`. */
    aspectRatio: number;
    /**
     * Ratio to use when `aspectRatio` is not a usable positive finite number.
     * Required — see the module note above: each surface owns its own default.
     */
    fallbackAspectRatio: number;
    maxWidth: number;
    maxHeight: number;
}

export interface RatioPreservingBoxSize {
    width: number;
    height: number;
}

/**
 * Ratio-preserving px size for a media box capped in BOTH dimensions.
 *
 * Same dual-cap rule as `getRatioPreservingMediaWidthCss` above, in numbers:
 * width is the single driven dimension, picked as
 * `min(maxWidth, maxHeight * ratio)` so the derived height
 * (`width / ratio`) can never exceed `maxHeight`. The returned box therefore
 * always has the media's own ratio, and an inner `object-fit: cover` never
 * crops.
 *
 * Numbers rather than a CSS expression because both budgets here are plain px
 * known at render time (no viewport units → no need for a CSS `min()`/`calc()`,
 * and no percentage-height resolution question for the children).
 *
 * Like its CSS sibling, this helper does NOT own the fallback value: the
 * caller passes `fallbackAspectRatio` (chat's is `1`, community's and the
 * comment thumbnail's `9/16`). It only applies the guard, because an
 * unusable ratio (`0` / negative / `NaN` / `Infinity`) would otherwise
 * produce a zero-width — invisible — box, and `MediaInfoEntity` really can
 * carry `aspectRatio: 0` (`MediaInfoRemapper.fromDto` defaults an absent DTO
 * value to `0`, `mediaInfoRemapper.ts:21`).
 */
export const getRatioPreservingBoxSize = ({
    aspectRatio,
    fallbackAspectRatio,
    maxWidth,
    maxHeight,
}: RatioPreservingBoxSizeOptions): RatioPreservingBoxSize => {
    const ratio =
        Number.isFinite(aspectRatio) && aspectRatio > 0
            ? aspectRatio
            : fallbackAspectRatio;
    const width = Math.min(maxWidth, maxHeight * ratio);
    return { width, height: width / ratio };
};
